/**
 * SessionContext — app-wide session memory (v0.1.19 + v0.1.27 dirty-flag).
 *
 * Distinct from CartContext (which owns the active CartDto + its mutations
 * inside the RAO route tree). SessionContext is the *shell-level* memory that
 * lets an officer cross between modules without losing their place:
 *
 *   - `activeCartId` — last cart touched (surfaces the "Resume cart" chip in
 *     TopBar; hydrated from localStorage so a Teams tab remount doesn't lose
 *     the in-flight cart).
 *   - `lastCifId` — last CIF viewed; drives breadcrumb "Back to customer".
 *   - `lastTaskId` — task that brought the user into the current screen
 *     (used by back-button to return to /rtm with the right filter).
 *
 * Storage: `localStorage['pcb.session.<upn>']` — per-officer so a shared
 * kiosk (RLQ) doesn't cross-pollinate. Cleared on sign-out.
 *
 * No React re-renders on localStorage writes; we only persist when a setter
 * fires, and we read once on mount.
 *
 * v0.1.27 additions (Bill 2026-04-23): per-form dirty tracking. Any form can
 * call `useFormGuard(id, isDirty)` to register/unregister its dirty state.
 * NavGuardModal consumes `isDirty` (aggregate) via React Router's useBlocker
 * to gate in-app navigations; the Teams registerBeforeUnloadHandler + the
 * classic window beforeunload event consume it for the "X" / reload path.
 * The aggregate flag is `dirtyIds.size > 0` so multiple concurrent forms
 * coexist safely — the guard only drops when ALL are clean.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { setUnloadGuardPredicate } from '@/services/teamsMultiWindow';

export interface SessionState {
  activeCartId: string | null;
  lastCifId: string | null;
  lastTaskId: string | null;
}

const EMPTY: SessionState = {
  activeCartId: null,
  lastCifId: null,
  lastTaskId: null,
};

export interface SessionContextValue extends SessionState {
  setActiveCart(cartId: string | null): void;
  setLastCif(cifId: string | null): void;
  setLastTask(taskId: string | null): void;
  clear(): void;
  /** True when ANY registered form has unsaved changes. */
  isDirty: boolean;
  /** Register/unregister a form's dirty state. `id` must be stable per form. */
  setDirtyFlag(id: string, dirty: boolean): void;
  /** Drop all dirty flags — call on successful save/submit or after a
   *  confirmed "Discard & Leave". */
  clearAllDirty(): void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function storageKey(upn: string | null | undefined): string | null {
  if (!upn) return null;
  return `pcb.session.${upn.toLowerCase()}`;
}

function readSnapshot(upn: string | null | undefined): SessionState {
  const key = storageKey(upn);
  if (!key || typeof window === 'undefined') return EMPTY;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<SessionState>;
    return {
      activeCartId: typeof parsed.activeCartId === 'string' ? parsed.activeCartId : null,
      lastCifId: typeof parsed.lastCifId === 'string' ? parsed.lastCifId : null,
      lastTaskId: typeof parsed.lastTaskId === 'string' ? parsed.lastTaskId : null,
    };
  } catch {
    return EMPTY;
  }
}

function writeSnapshot(upn: string | null | undefined, state: SessionState): void {
  const key = storageKey(upn);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota / disabled localStorage — silent; in-memory state still works.
  }
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const upn = user?.upn ?? null;

  const [state, setState] = useState<SessionState>(() => readSnapshot(upn));

  // Dirty-form registry. We track the Set size in React state for renders,
  // and keep a ref to the live set for fast mutation in setDirtyFlag. Using a
  // ref + size-counter avoids allocating a new Set on every keystroke.
  const dirtySetRef = useRef<Set<string>>(new Set());
  const [dirtyCount, setDirtyCount] = useState<number>(0);

  // When the officer changes (sign-out → sign-in), rehydrate from their slot.
  useEffect(() => {
    setState(readSnapshot(upn));
  }, [upn]);

  const persist = useCallback((next: SessionState): void => {
    setState(next);
    writeSnapshot(upn, next);
  }, [upn]);

  const setActiveCart = useCallback((cartId: string | null): void => {
    persist({ ...state, activeCartId: cartId });
  }, [persist, state]);

  const setLastCif = useCallback((cifId: string | null): void => {
    persist({ ...state, lastCifId: cifId });
  }, [persist, state]);

  const setLastTask = useCallback((taskId: string | null): void => {
    persist({ ...state, lastTaskId: taskId });
  }, [persist, state]);

  const clear = useCallback((): void => {
    persist(EMPTY);
  }, [persist]);

  const setDirtyFlag = useCallback((id: string, dirty: boolean): void => {
    const set = dirtySetRef.current;
    const had = set.has(id);
    if (dirty && !had) {
      set.add(id);
      setDirtyCount(set.size);
    } else if (!dirty && had) {
      set.delete(id);
      setDirtyCount(set.size);
    }
  }, []);

  const clearAllDirty = useCallback((): void => {
    if (dirtySetRef.current.size === 0) return;
    dirtySetRef.current.clear();
    setDirtyCount(0);
  }, []);

  const isDirty = dirtyCount > 0;

  // Tell the Teams multi-window close-guard when in-flight work exists.
  // The predicate fires on window close / reload / Teams sidebar tab switch;
  // we want the prompt when EITHER an active cart is pinned (the v0.1.23
  // behavior) OR any form has unsaved changes (v0.1.27).
  useEffect(() => {
    setUnloadGuardPredicate(() => isDirty || state.activeCartId != null);
  }, [isDirty, state.activeCartId]);

  // Teams window "X" close-guard (Bill req 2026-04-23). The Teams SDK's
  // registerBeforeUnloadHandler is the only signal we get when the user
  // closes the Teams window (vs. navigating within the app). Browsers/Teams
  // force a system-level confirm dialog for this path — our custom React
  // modal cannot intercept it (see Gemini guide §"The Hard Reality").
  // So we use window.confirm() — ugly, but it's the only sync option Teams
  // respects before firing readyToUnload().
  //
  // BACKLOG-1297 kept this ONE window.confirm() while replacing every other
  // native dialog in the app with in-app UI. The app is being torn down here:
  // by the time this handler runs the surface is going away, so a React modal
  // has nothing to paint into and nobody to wait for. Worth knowing: the
  // Teams webview suppresses native dialogs, so this confirm most likely
  // returns false without showing anything, which means readyToUnload() isn't
  // called and Teams falls back on its own unload timeout. Changing that is a
  // behaviour decision about Teams close (does an in-flight cart block an app
  // eviction at all?), not a UI cleanup — flagged for Bill, left as-is here.
  useEffect(() => {
    let cancelled = false;
    let uninstall: (() => void) | null = null;

    (async () => {
      try {
        const teamsJs = await import('@microsoft/teams-js');
        // As of teams-js v2.0 the canonical namespace is `teamsCore`; the old
        // top-level `app.registerBeforeUnloadHandler` was deprecated.
        // Feature-detect both paths so we stay compatible with older hosts
        // and browser-direct dev sessions.
        const register =
          (teamsJs as unknown as { teamsCore?: { registerBeforeUnloadHandler?: (h: (cb: () => void) => boolean) => void } })
            .teamsCore?.registerBeforeUnloadHandler;
        if (typeof register !== 'function') return;
        if (cancelled) return;

        const handler = (readyToUnload: () => void): boolean => {
          const dirty = isDirty || state.activeCartId != null;
          if (!dirty) {
            readyToUnload();
            return true;
          }
          // Sync prompt. MUST be synchronous — Teams treats any async work
          // as a hang and may force-close. No custom React UI possible here.
          const ok = typeof window !== 'undefined'
            ? window.confirm('You have unsaved changes. Close Teams anyway?')
            : true;
          if (ok) readyToUnload();
          return true;
        };

        register(handler);
        // Teams JS has no pair-matched unregister. The closest we can do is
        // overwrite with a no-op on cleanup so a re-mount doesn't double-fire.
        uninstall = () => {
          try {
            register(() => {
              // Clean — let Teams close immediately. Real guard now re-installed
              // by the next effect run.
              return false;
            });
          } catch { /* ignore */ }
        };
      } catch {
        // Not in Teams or SDK import failed; rely on window beforeunload fallback.
      }
    })();

    return () => {
      cancelled = true;
      if (uninstall) uninstall();
    };
  }, [isDirty, state.activeCartId]);

  const value = useMemo<SessionContextValue>(() => ({
    ...state,
    setActiveCart,
    setLastCif,
    setLastTask,
    clear,
    isDirty,
    setDirtyFlag,
    clearAllDirty,
  }), [state, setActiveCart, setLastCif, setLastTask, clear, isDirty, setDirtyFlag, clearAllDirty]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider/>');
  return ctx;
}
